import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CalculationService, DataService, DateService, HelperService, HTTPService } from 'app/services';
import { config } from 'app/constants';

import { currency } from 'app/constants/currency.constants';
import { datetimeFormat } from 'app/constants/date-time-format';
import { find } from 'lodash';
import { CurrencyPipe } from '@angular/common';

@Component({
  encapsulation: ViewEncapsulation.None,
  providers: [DialogService],
  selector: 'app-credit-or-edit-memo-payment',
  styleUrls: ['./credit-or-edit-memo-payment.component.scss'],
  templateUrl: './credit-or-edit-memo-payment.component.html',
})
export class CreditPaymentAddComponent implements OnInit {
  public RO = [];
  public usd = currency.USD;
  public validate: any = {};
  public paymentId: any;
  public focus: any = { collectedAmount: false };
  public maxDateRange: any = new Date();
  public payment: any = {
    _id: '',
    code: '',
    qboId: '',
    qboReference: {},
    collectedAmount: null,
    customer: {
      customerId: '',
      email: '',
      fullName: '',
      phone: '',
    },
    estimate: {
      card: 0,
      cash: 0,
      cheque: 0,
      creditPayment: 0,
      thirdParty: 0,
      code: '',
      estimateBy: '',
      estimateId: '',
      invCode: '',
      title: '',
      total: 0,
    },
    notes: '',
    history: [],
    appliedEstimates: [],
  };
  public paymentTime: any = '';
  public isQboConnected: any = false;
  public searchQuery = '';
  public tableConfig = {
    activeStatus: 'true',
    invoicePeriod: '',
    isFleetCustomer: null,
    lastInvoiceEnd: '',
    lastInvoiceStart: '',
    limit: config.pageSize,
    offset: 0,
    roCreatedPeriod: '',
    search: '',
    sortField: 'fullNameQBO',
    sortOrder: 1,
    tags: [],
  };
  public isProcessing = false;
  public isProcessingQBO = false;
  public format = datetimeFormat;

  //  estimate
  estimateSearchResults: any[] = [];
  isSearching: boolean = false;
  isLockOrderEnabled: boolean = false;
  collectedAmountError: string = '';
  defaultPaymentAmount = 0;
  defaultRos: any[] = [];
  changePaymentData: boolean = false;
  isPaymentDataPrivilgesEnabled: boolean = false;

  constructor(
    public httpService: HTTPService,
    public helperService: HelperService,
    public dataService: DataService,
    public dateService: DateService,
    public ref: DynamicDialogRef,
    private currencyPipe: CurrencyPipe,
    public dConfig: DynamicDialogConfig,
    public calculationService: CalculationService
  ) { }

  public ngOnInit() {
    const data = this.dConfig.data;
    this.isQboConnected = this.helperService.getCompany().qbo;
    // this.isQboConnected = true;

    if (data) {
      this.paymentId = data.memoId;
      this.payment = data;
      if (data.estimate && data.estimate.customer) {
        this.searchQuery = data.estimate?.customer?.displayName || data.estimate?.customer?.fullName;
        this.payment.customer = data.estimate.customer;
      }
      this.setPaymentDetails(data);
      this.getEstimates(data.estimate && data.estimate.customer.customerId);
    }

    this.getReviewConfiguration();
    this.changePaymentData = this.dataService.changePaymentData;
    this.isPaymentDataPrivilgesEnabled = this.dataService.isPaymentDataPrivilgesEnabled;
  }

  public getCustomerCreditMemoById() {
    this.httpService
      .getCreditMemoById(this.paymentId)
      .then((response: any) => {
        if (response.linkedEstimate) {
          this.payment.estimate =
            find(this.estimateSearchResults, {
              code: response.linkedEstimate && response.linkedEstimate.code,
            }) || {};
        }
        this.defaultRos = this.helperService.deepCopy(this.estimateSearchResults);
        this.payment.collectedAmount = this.calculationService.roundOff(response.amount);
        this.defaultPaymentAmount = this.payment.collectedAmount;
        this.payment.notes = response.notes || '';
        this.payment.history = (response.history && response.history.length && response.history.reverse()) || [];
        this.payment.appliedEstimates = response.appliedEstimates || [];
        this.payment.qboReference = response.qboReference;
        this.payment.createdFrom = response.createdFrom;
        if (!!response.date) {
          if (this.helperService.isAppleDevice()) {
            response.date = this.dateService.madeDateStringIOSCompatible(response.date);
          }

          this.payment.paymentDate = new Date(response.date);
          this.paymentTime = new Date(response.date);
        } else {
          this.payment.paymentDate = this.dateService.getCurrentDateTimeTz(new Date(response.createdAt).getTime());
          this.paymentTime = this.payment.paymentDate;
        }
      })
      .catch((err) => this.helperService.showToaster('error', err));
  }

  /** Populate Payment Details */
  public setPaymentDetails(payment: any) {
    const estimate = payment.estimate || {};
    const customer = payment.customer || {};
    this.payment = {
      _id: payment._id,
      code: estimate.code || '',
      notes: payment.notes,
      collectedAmount: payment.amount,
      customer: {
        customerId: customer.customerId,
        fullName: customer.fullName,
        email: customer.email,
        phone: customer.phone,
        phone2: customer.phone2,
        notes: customer.notes,
        displayName: customer.displayName,
      },
      estimate: {},
    };
  }

  getActivityDateTime(createdAt: any) {
    return this.dateService.formatDate(this.dateService.getCurrentDateTimeTz(new Date(createdAt).getTime()), this.format.mmmdyyhhmmaa);
  }

  /** Getting amount from the input box and calculating the remaining balance */
  public handleChange(value: any, field: string, index?) {
    if (field === 'collectedAmount') {
      this.collectedAmountError = '';
      this.payment[field] = !isNaN(value.replace(',', '')) ? Number(value.replace(',', '')) : value;
    } else if (field === 'appliedAmt') {
      if (this.payment.appliedEstimates[index].appliedAmt != Number(value)) {
        this.payment.appliedEstimates[index]['isUpdated'] = true;
      }
      this.payment.appliedEstimates[index].appliedAmt = !isNaN(value.replace(',', '')) ? Number(value.replace(',', '')) : value;
    } else {
      this.payment[field] = value;
    }
  }

  public getEstimates(customerId) {
    this.httpService.getCustomerEstimates(customerId).then(
      (estimates: any[]) => {
        estimates.forEach((estimate) => {
          const name = `RO# ${estimate.code}`;
          estimate.name = name;
          estimate.value = estimate._id;
        });
        this.estimateSearchResults = estimates;

        if (Object.keys(this.payment.estimate).length == 0) {
          this.payment.estimate =
            find(this.estimateSearchResults, {
              code: this.payment.code,
            }) || {};
        }
        this.defaultRos = this.helperService.deepCopy(estimates);
        if (this.paymentId) {
          this.getCustomerCreditMemoById();
        }
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  public handleSelectRO(RO) {
    this.payment.estimate = RO;
  }

  public onDateTimeChange(date: any) {
    this.payment.paymentDate = date;
  }

  /** Getting review configuration */
  public getReviewConfiguration() {
    this.httpService.getReviewConfiguration().then(
      (configuration) => {
        this.paymentTime = this.dateService.getCurrentDateTimeTz(configuration.serverTime);
        this.payment.paymentDate = this.dateService.getCurrentDateTimeTz(configuration.serverTime);
        const maxDateRange = this.dateService.getCurrentDateTimeTz(configuration.serverTime);
        this.maxDateRange = new Date(maxDateRange);
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  public onSearchSelect(event: any) {
    if (event._id !== 'new-customer') {
      this.searchQuery = event.fullName;
      this.tableConfig.offset = 0;
      this.payment.customer = event;
    } else {
      this.searchQuery = null;
    }
  }

  public closePaymentModal(data?: any) {
    this.ref.close(data);
  }

  public handleFocus(type, index?) {
    if (index >= 0) {
      this.payment.appliedEstimates[index].focus = true;
    }
    this.focus[type] = true;
    this.validate.amount = '';
    if (isNaN(this.payment.collectedAmount)) {
      this.payment.collectedAmount = 0;
    }
  }

  handleBlur(type: string, value?) {
    if (type === 'collectedAmount') {
      this.focus.collectedAmount = false;
    }
    if (value >= 0 && type === 'appliedAmt') {
      this.payment.appliedEstimates[value].focus = false;
    } else if (type === 'collectedAmount' && this.defaultPaymentAmount < value && this.paymentId && this.payment?.appliedEstimates?.length) {
      this.collectedAmountError = `Credit Amount cannot be greater than ${this.currencyPipe.transform(this.defaultPaymentAmount || 0, this.usd)}`;
    }
  }

  public onChangeNote(event) {
    if (this.paymentId) {
      this.payment.customer.notes = event;
    }
  }

  public onSubmit(isUpdateQBO) {
    this.createOrUpdateTransaction(isUpdateQBO);
  }

  /** Making a credit memo payment */
  public createOrUpdateTransaction(isUpdateQBO) {
    if (!this.validation()) {
      return;
    }
    const payload = {
      _id: this.paymentId,
      amount: this.payment.collectedAmount,
      customer: this.payment.customer,
      date: `${this.dateService.formatDate(this.payment.paymentDate, datetimeFormat.yyymmdd)} ${this.dateService.formatDate(this.paymentTime, datetimeFormat.hhmm)}`,
      linkedEstimate: this.payment.estimate,
      notes: this.payment.notes,
      postToQBO: isUpdateQBO,
      appliedEstimates: this.payment.appliedEstimates,
    };
    if (!isUpdateQBO) {
      if (this.isProcessing) return;
      delete payload.postToQBO;
      this.isProcessing = true;
    } else {
      if (this.isProcessingQBO) return;
      this.isProcessingQBO = true;
    }
    if (this.paymentId) {
      payload._id;
      this.httpService
        .updateMemo(payload)
        .then((res) => {
          if (!(res.error && res.error.error == true)) {
            this.helperService.showToaster('success', `${payload.postToQBO ? 'Credit memo updated and posted to qbo' : 'Credit memo updated'}`);
          } else {
            this.dataService.sendData({
              type: 'app-toaster',
              status: 'error',
              message: {
                heading: res.error.msg.heading ? res.error.msg.heading : 'QBO Error',
                body: `Credit memo updated but ${res.error.msg.body ? res.error.msg.body : 'unexpected error, try again later!'}`,
              },
            });
          }
          this.dataService.sendData(this.dataService.typePassData('customer-memo-list', null));
          this.dataService.sendData(this.dataService.typePassData('payment-list', {}));
          this.getCustomerCreditMemoById();
          // this.closePaymentModal();
        })
        .catch((err) => {
          if (typeof err === 'string') {
            this.dataService.sendData({
              type: 'app-toaster',
              status: 'error',
              message: {
                heading: 'Error',
                body: err,
              },
            });
          } else {
            this.dataService.sendData({
              type: 'app-toaster',
              status: 'error',
              message:
                err.data && err.data.message
                  ? {
                    heading: 'Error',
                    body: err.data && err.data.message,
                  }
                  : err.msg,
            });
          }
        })
        .finally(() => {
          this.isProcessing = false;
          this.isProcessingQBO = false;
        });
    } else {
      delete payload._id;
      this.httpService
        .createMemo(payload)
        .then((res) => {
          this.paymentId = res.error && res.error.error == true ? res.response._id : res._id;
          if (!(res.error && res.error.error == true)) {
            this.helperService.showToaster('success', `${payload.postToQBO ? 'Credit memo created and posted to qbo' : 'Credit memo created'}`);
          } else {
            this.dataService.sendData({
              type: 'app-toaster',
              status: 'error',
              message: {
                heading: res.error.msg.heading,
                body: `Credit memo created but ${res.error.msg.body}`,
              },
            });
          }
          this.dataService.sendData(this.dataService.typePassData('customer-memo-list', null));
          this.getCustomerCreditMemoById();
          // this.closePaymentModal();
        })
        .catch((err) => {
          if (typeof err === 'string') {
            this.dataService.sendData({
              type: 'app-toaster',
              status: 'error',
              message: {
                heading: 'Error',
                body: err,
              },
            });
          } else {
            this.dataService.sendData({
              type: 'app-toaster',
              status: 'error',
              message:
                err.data && err.data.message
                  ? {
                    heading: 'Error',
                    body: err.data && err.data.message,
                  }
                  : err.msg,
            });
          }
        })
        .finally(() => {
          this.isProcessing = false;
          this.isProcessingQBO = false;
        });
    }
  }

  public saveToQuickBook(isUpdateQBO = false) {
    this.onSubmit(isUpdateQBO);
  }

  /** Validate payment data */
  public validation() {
    this.focus.collectedAmount = false;
    if (this.payment.collectedAmount === '.') {
      this.payment.collectedAmount = 0;
    }

    const isFormValid = true;
    this.validate = {};

    if (!this.payment.collectedAmount) {
      this.helperService.showToaster('error', `Please select a valid amount`);
      return false;
    }

    if (this.paymentId) {
      const appliedEst = this.payment.appliedEstimates;
      const totalPayment = appliedEst.reduce((sum, item) => sum + item.appliedAmt, 0);
      if (this.payment.collectedAmount < totalPayment) {
        this.helperService.showToaster('error', `Collected amount must be greater than applied amount`);
        return false;
      }
    }
    if (!this.searchQuery) {
      this.helperService.showToaster('error', `Please select a customer`);
      return false;
    }

    if (this.payment.paymentDate && this.paymentTime) {
      const date = this.dateService.formatDate(this.payment.paymentDate, datetimeFormat.yyymmdd);
      const time = this.dateService.formatDate(this.paymentTime, datetimeFormat.hhmm);
      let paymentDate: any = new Date(`${date} ${time}`);

      if (this.helperService.isAppleDevice()) {
        paymentDate = new Date(this.dateService.madeDateStringIOSCompatible(`${date} ${time}`));
      } else {
        paymentDate = new Date(`${date} ${time}`);
      }

      if (paymentDate === 'Invalid Date') {
        this.helperService.showToaster('error', `Please select a valid date`);
        return false;
      }
    }

    if (this.collectedAmountError) {
      return false;
    }
    return isFormValid;
  }

  showSelected(type: string) {
    // if (type === 'order') {
    //   if (this.payment.estimate && this.payment.estimate.estimateId) {
    //     this.isOrderEditable = false;
    //   }
    // } else {
    //   if (this.payment.customer && this.payment.customer.customerId) {
    //     this.isCustomerEditable = false;
    //   }
    // }
  }
  public onEstimateSearchSelect(event) {
    this.payment.estimate = event;
  }
  public clearSearchResult(type) {
    this.estimateSearchResults = [];
  }

  public onEstimateSearch(value: string) {
    if (!value) return;
    let query = '';
    query = value.includes('RO#') ? value.replace('RO# ', '') : value;
    this.estimateSearchResults = [...this.defaultRos.filter(({ code }) => new RegExp(query, 'i').test(code))];
  }

  isOrderResctricted(restriction: any = {}) {
    if (!this.isLockOrderEnabled) return true;

    if (restriction) {
      return this.helperService.isLockOrderSessionExist(restriction.token) || !restriction.status || restriction.status === 'readOnly';
    } else {
      return false;
    }
  }

  roPostToQBO(estimate) {
    this.httpService.syncCompleteInvoiceInQBO(estimate.estimateId).then(
      (res: any) => {
        if (res.msg.discontinueQuickbooksCardPayPosting) {
          this.dataService.sendData({
            type: 'app-toaster',
            status: 'success',
            message: {
              heading: 'Invoice has been successfully posted',
              body: `<div>Posting of card payments has been turned "Off"</div><div>Contact AutoLeap support for help</div>`,
            },
          });
        } else {
          this.helperService.showToaster('success', 'Successfully posted to QuickBooks');
        }

        this.dataService.sendData(this.dataService.typePassData('customer-memo-list', null));
        this.dataService.sendData(this.dataService.typePassData('payment-list', {}));
        this.getCustomerCreditMemoById();
      },
      (err) => {
        if (typeof err === 'string') {
          this.dataService.sendData({
            type: 'app-toaster',
            status: 'error',
            message: {
              heading: 'Error',
              body: err,
            },
          });
        } else {
          this.dataService.sendData({
            type: 'app-toaster',
            status: 'error',
            message:
              err.data && err.data.message
                ? {
                  heading: 'Error',
                  body: err.data && err.data.message,
                }
                : err.msg,
          });
        }
      }
    );
    // .finally(() => {

    // });
  }

  validateChangePaymentData() {
    if (this.paymentId && !this.changePaymentData && this.isPaymentDataPrivilgesEnabled) {
      return true;
    }
    return false;
  }
}
